import React, {
  useContext,
  useEffect,
  useState
} from "react";

import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Checkbox,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";

import {
  AudiotrackOutlined,
  DescriptionOutlined,
  InsertDriveFileOutlined,
  OpenInNew,
  PhotoOutlined,
  Refresh,
  VideocamOutlined
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ForbiddenPage from "../../components/ForbiddenPage";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles
  },

  summaryCard: {
    height: "100%",
    padding: theme.spacing(2),
    borderRadius: 12
  },

  summaryLabel: {
    fontSize: 12,
    opacity: 0.72,
    marginBottom: 4
  },

  summaryValue: {
    fontSize: 22,
    fontWeight: 700
  },

  filters: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: 12
  },

  mediaCard: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    borderRadius: 12,
    overflow: "hidden"
  },

  preview: {
    height: 105,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.04)"
        : "#f5f6f8",
    overflow: "hidden"
  },

  previewImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover"
  },

  mediaIcon: {
    fontSize: 54,
    opacity: 0.55
  },

  filename: {
    fontWeight: 600,
    fontSize: 13,
    lineHeight: 1.35,
    wordBreak: "break-word",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    minHeight: 36
  },

  meta: {
    fontSize: 12,
    opacity: 0.72,
    marginTop: 5
  },

  chips: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
    marginTop: 10
  },

  empty: {
    padding: theme.spacing(6),
    textAlign: "center",
    opacity: 0.75
  },

  pagination: {
    marginTop: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 14
  },

  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(6)
  },

  periodActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: theme.spacing(2)
  },

  selectionBar: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 12,
    border: `2px solid ${theme.palette.primary.main}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12
  },

  selectionInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 3
  }
}));

const initialData = {
  items: [],
  count: 0,
  page: 1,
  limit: 24,
  hasMore: false,
  totalStorageBytes: 0,
  conversationBytes: 0,
  companyBytes: 0,
  filteredBytes: 0,
  sources: []
};

const formatBytes = value => {
  const bytes = Number(value || 0);

  if (bytes <= 0) return "0 B";

  const units = [
    "B",
    "KB",
    "MB",
    "GB",
    "TB"
  ];

  const index = Math.min(
    Math.floor(
      Math.log(bytes) /
      Math.log(1024)
    ),
    units.length - 1
  );

  const result =
    bytes /
    Math.pow(1024, index);

  return `${result.toLocaleString(
    "pt-BR",
    {
      maximumFractionDigits:
        index >= 3 ? 2 : 1
    }
  )} ${units[index]}`;
};

const typeLabel = type => {
  const labels = {
    image: "Foto",
    video: "Vídeo",
    audio: "Áudio",
    document: "Documento",
    other: "Outro"
  };

  return labels[type] || type;
};

const MediaPreview = ({ item, classes }) => {
  if (item.type === "image") {
    return (
      <img
        src={item.url}
        alt={item.name}
        className={classes.previewImage}
        loading="lazy"
      />
    );
  }

  if (item.type === "video") {
    return (
      <VideocamOutlined
        className={classes.mediaIcon}
      />
    );
  }

  if (item.type === "audio") {
    return (
      <AudiotrackOutlined
        className={classes.mediaIcon}
      />
    );
  }

  if (item.type === "document") {
    return (
      <DescriptionOutlined
        className={classes.mediaIcon}
      />
    );
  }

  return (
    <InsertDriveFileOutlined
      className={classes.mediaIcon}
    />
  );
};

const Storage = () => {
  const classes = useStyles();
  const { user } =
    useContext(AuthContext);

  const [data, setData] =
    useState(initialData);

  const [loading, setLoading] =
    useState(true);

  const [refreshKey, setRefreshKey] =
    useState(0);

  const [page, setPage] =
    useState(1);

  const [search, setSearch] =
    useState("");

  const [scope, setScope] =
    useState("all");

  const [type, setType] =
    useState("all");

  const [source, setSource] =
    useState("");

  const [sort, setSort] =
    useState("size-desc");

  const [dateFrom, setDateFrom] =
    useState("");

  const [dateTo, setDateTo] =
    useState("");

  const [minSizeMB, setMinSizeMB] =
    useState("");

  const [maxSizeMB, setMaxSizeMB] =
    useState("");

  const [selectAllFiltered, setSelectAllFiltered] =
    useState(false);

  const [selectedItems, setSelectedItems] =
    useState({});

  const selectedList =
    Object.values(selectedItems);

  const selectedCount =
    selectedList.length;

  const selectedBytes =
    selectedList.reduce(
      (total, item) =>
        total +
        Number(item.size || 0),
      0
    );

  const handleBuyStorage = async () => {
    try {
      const { data: info } =
        await api.get(
          "/subscription/storage"
        );

      const pricePerGB =
        Number(
          info?.pricePerGB || 0
        );

      if (
        !Number.isFinite(pricePerGB) ||
        pricePerGB <= 0
      ) {
        window.alert(
          "A compra de armazenamento ainda não está disponível para o seu plano."
        );

        return;
      }

      const formattedPrice =
        pricePerGB.toLocaleString(
          "pt-BR",
          {
            style: "currency",
            currency: "BRL"
          }
        );

      const answer =
        window.prompt(
          `O valor é ${formattedPrice} por GB/mês.\n\nQuantos GB extras deseja contratar?`,
          "5"
        );

      if (answer === null) {
        return;
      }

      const storageGB =
        Number(answer);

      if (
        !Number.isInteger(storageGB) ||
        storageGB <= 0
      ) {
        window.alert(
          "Informe uma quantidade inteira de GB maior que zero."
        );

        return;
      }

      const total =
        (
          pricePerGB *
          storageGB
        ).toLocaleString(
          "pt-BR",
          {
            style: "currency",
            currency: "BRL"
          }
        );

      const confirmed =
        window.confirm(
          `Contratar +${storageGB} GB por ${total}/mês?\n\nA cobrança será separada da assinatura principal.`
        );

      if (!confirmed) {
        return;
      }

      const { data: result } =
        await api.post(
          "/subscription/storage",
          {
            storageGB
          }
        );

      if (result?.existing) {
        window.alert(
          "Já existe uma cobrança de armazenamento em aberto. Você será direcionado ao Financeiro para concluir o pagamento."
        );
      } else {
        window.alert(
          "Cobrança de armazenamento criada. Você será direcionado ao Financeiro para realizar o pagamento."
        );
      }

      window.location.href =
        "/financeiro";
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    if (user?.profile !== "admin") {
      return undefined;
    }

    const timeout =
      setTimeout(async () => {
        setLoading(true);

        try {
          const [
            sortBy,
            order
          ] = sort.split("-");

          const { data: response } =
            await api.get(
              "/storage/media",
              {
                params: {
                  page,
                  limit: 36,
                  search,
                  scope,
                  type,
                  source:
                    scope === "company"
                      ? source
                      : "",
                  sortBy,
                  order,
                  dateFrom:
                    dateFrom || undefined,
                  dateTo:
                    dateTo || undefined,
                  minSizeMB:
                    minSizeMB !== ""
                      ? minSizeMB
                      : undefined,
                  maxSizeMB:
                    maxSizeMB !== ""
                      ? maxSizeMB
                      : undefined
                }
              }
            );

          setData({
            ...initialData,
            ...response
          });
        } catch (err) {
          toastError(err);
        } finally {
          setLoading(false);
        }
      }, 300);

    return () =>
      clearTimeout(timeout);
  }, [
    user,
    page,
    search,
    scope,
    type,
    source,
    sort,
    dateFrom,
    dateTo,
    minSizeMB,
    maxSizeMB,
    refreshKey
  ]);

  const resetPage = setter => value => {
    setter(value);
    setPage(1);
  };

  const formatDateInput = date => {
    const year = date.getFullYear();
    const month = String(
      date.getMonth() + 1
    ).padStart(2, "0");

    const day = String(
      date.getDate()
    ).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const selectOlderThan = days => {
    const cutoff = new Date();

    cutoff.setDate(
      cutoff.getDate() - days
    );

    setDateFrom("");
    setDateTo(
      formatDateInput(cutoff)
    );

    setPage(1);
    setSelectAllFiltered(false);
  };

  const clearPeriod = () => {
    setDateFrom("");
    setDateTo("");
    setPage(1);
    setSelectAllFiltered(false);
  };

  const buildDeleteFilter = () => ({
    type,
    search,

    dateFrom:
      dateFrom || undefined,

    dateTo:
      dateTo || undefined,

    minSizeMB:
      minSizeMB !== ""
        ? Number(minSizeMB)
        : undefined,

    maxSizeMB:
      maxSizeMB !== ""
        ? Number(maxSizeMB)
        : undefined
  });

  const toggleSelectedItem = item => {
    if (
      item.scope !== "conversation"
    ) {
      return;
    }

    setSelectedItems(current => {
      const next = {
        ...current
      };

      if (
        next[item.relativePath]
      ) {
        delete next[
          item.relativePath
        ];
      } else {
        next[item.relativePath] =
          item;
      }

      return next;
    });
  };

  const clearManualSelection = () => {
    setSelectedItems({});
  };

  const selectCurrentPage = () => {
    setSelectedItems(current => {
      const next = {
        ...current
      };

      data.items
        .filter(
          item =>
            item.scope ===
            "conversation"
        )
        .forEach(item => {
          next[item.relativePath] =
            item;
        });

      return next;
    });
  };

  const handleDeleteSelected = async () => {
    const paths =
      selectedList.map(
        item =>
          item.relativePath
      );

    if (paths.length === 0) {
      return;
    }

    try {
      const { data: preview } =
        await api.post(
          "/storage/media/delete-selected",
          {
            relativePaths:
              paths,

            dryRun: true
          }
        );

      const selectedFiles =
        Number(
          preview?.selectedFiles || 0
        );

      const bytes =
        Number(
          preview?.selectedBytes || 0
        );

      if (selectedFiles === 0) {
        window.alert(
          "Nenhum dos arquivos selecionados foi encontrado."
        );

        clearManualSelection();
        return;
      }

      const confirmed =
        window.confirm(
          `CONFIRMAR EXCLUSÃO\n\n` +
          `${selectedFiles.toLocaleString("pt-BR")} arquivo(s) selecionado(s)\n` +
          `${formatBytes(bytes)} serão liberados\n\n` +
          `As mensagens continuarão no atendimento.\n` +
          `Somente as mídias selecionadas serão removidas.\n\n` +
          `Esta ação não pode ser desfeita.`
        );

      if (!confirmed) {
        return;
      }

      const { data: result } =
        await api.post(
          "/storage/media/delete-selected",
          {
            relativePaths:
              paths,

            dryRun: false,
            confirm: true,

            expectedSelectedFiles:
              selectedFiles,

            expectedSelectedBytes:
              bytes
          }
        );

      window.alert(
        `${Number(
          result?.deletedFiles || 0
        ).toLocaleString("pt-BR")} arquivo(s) removido(s).\n` +
        `${formatBytes(
          Number(
            result?.deletedBytes || 0
          )
        )} liberados.`
      );

      clearManualSelection();

      setRefreshKey(
        current =>
          current + 1
      );
    } catch (err) {
      toastError(err);

      setRefreshKey(
        current =>
          current + 1
      );
    }
  };

  const handleDeleteFiltered = async () => {
    if (scope !== "conversation") {
      window.alert(
        "Para excluir em massa, selecione primeiro Mídias de conversa."
      );
      return;
    }

    try {
      const { data: preview } =
        await api.post(
          "/storage/media/delete-filter",
          {
            ...buildDeleteFilter(),
            dryRun: true
          }
        );

      const selectedFiles =
        Number(
          preview?.selectedFiles || 0
        );

      const selectedBytes =
        Number(
          preview?.selectedBytes || 0
        );

      if (selectedFiles === 0) {
        window.alert(
          "Nenhuma mídia de conversa encontrada para este filtro."
        );

        setSelectAllFiltered(false);
        return;
      }

      const periodText =
        dateFrom || dateTo
          ? `\nPeríodo: ${dateFrom || "início"} até ${dateTo || "hoje"}`
          : "";

      const confirmed =
        window.confirm(
          `CONFIRMAR EXCLUSÃO\n\n` +
          `${selectedFiles.toLocaleString("pt-BR")} arquivo(s)\n` +
          `${formatBytes(selectedBytes)} serão liberados` +
          periodText +
          `\n\nAs mensagens continuarão no atendimento.` +
          `\nSomente os arquivos de mídia serão removidos.` +
          `\n\nEsta ação não pode ser desfeita.`
        );

      if (!confirmed) {
        return;
      }

      const { data: result } =
        await api.post(
          "/storage/media/delete-filter",
          {
            ...buildDeleteFilter(),

            dryRun: false,
            confirm: true,

            expectedSelectedFiles:
              selectedFiles,

            expectedSelectedBytes:
              selectedBytes
          }
        );

      const deletedFiles =
        Number(
          result?.deletedFiles || 0
        );

      const deletedBytes =
        Number(
          result?.deletedBytes || 0
        );

      const failedFiles =
        Number(
          result?.failedFiles || 0
        );

      let message =
        `${deletedFiles.toLocaleString("pt-BR")} arquivo(s) removido(s).\n` +
        `${formatBytes(deletedBytes)} liberados.`;

      if (failedFiles > 0) {
        message +=
          `\n\n${failedFiles} arquivo(s) não puderam ser removidos e foram preservados.`;
      }

      window.alert(message);

      setSelectAllFiltered(false);
      setPage(1);

      setRefreshKey(
        current => current + 1
      );
    } catch (err) {
      toastError(err);

      setSelectAllFiltered(false);

      setRefreshKey(
        current => current + 1
      );
    }
  };

  useEffect(() => {
    setSelectAllFiltered(false);
    setSelectedItems({});
  }, [
    search,
    scope,
    type,
    source,
    sort,
    dateFrom,
    dateTo,
    minSizeMB,
    maxSizeMB
  ]);

  if (
    user?.profile !== "admin"
  ) {
    return <ForbiddenPage />;
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>Armazenamento</Title>

        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              setRefreshKey(
                current => current + 1
              );
            }}
          >
            Atualizar
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={handleBuyStorage}
            title="Contratar armazenamento extra mensal"
          >
            Comprar armazenamento
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper
        className={classes.mainPaper}
        elevation={0}
      >
        <Grid
          container
          spacing={2}
        >
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
          >
            <Paper
              className={classes.summaryCard}
              variant="outlined"
            >
              <Typography
                className={classes.summaryLabel}
              >
                Armazenamento total
              </Typography>

              <Typography
                className={classes.summaryValue}
              >
                {formatBytes(
                  data.totalStorageBytes
                )}
              </Typography>
            </Paper>
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={3}
          >
            <Paper
              className={classes.summaryCard}
              variant="outlined"
            >
              <Typography
                className={classes.summaryLabel}
              >
                Mídias de conversa
              </Typography>

              <Typography
                className={classes.summaryValue}
              >
                {formatBytes(
                  data.conversationBytes
                )}
              </Typography>
            </Paper>
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={3}
          >
            <Paper
              className={classes.summaryCard}
              variant="outlined"
            >
              <Typography
                className={classes.summaryLabel}
              >
                Arquivos da empresa
              </Typography>

              <Typography
                className={classes.summaryValue}
              >
                {formatBytes(
                  data.companyBytes
                )}
              </Typography>
            </Paper>
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={3}
          >
            <Paper
              className={classes.summaryCard}
              variant="outlined"
            >
              <Typography
                className={classes.summaryLabel}
              >
                Resultado filtrado
              </Typography>

              <Typography
                className={classes.summaryValue}
              >
                {formatBytes(
                  data.filteredBytes
                )}
              </Typography>

              <Typography
                variant="caption"
              >
                {data.count} arquivo(s)
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Paper
          className={classes.filters}
          variant="outlined"
        >
          <Grid
            container
            spacing={2}
            alignItems="center"
          >
            <Grid
              item
              xs={12}
              md={4}
            >
              <TextField
                label="Pesquisar arquivo"
                variant="outlined"
                size="small"
                fullWidth
                value={search}
                onChange={event => {
                  setSearch(
                    event.target.value
                  );
                  setPage(1);
                }}
              />
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
              md={2}
            >
              <FormControl
                variant="outlined"
                size="small"
                fullWidth
              >
                <InputLabel>
                  Área
                </InputLabel>

                <Select
                  value={scope}
                  onChange={event => {
                    setScope(
                      event.target.value
                    );
                    setSource("");
                    setPage(1);
                  }}
                  label="Área"
                >
                  <MenuItem value="all">
                    Tudo
                  </MenuItem>

                  <MenuItem value="conversation">
                    Conversas
                  </MenuItem>

                  <MenuItem value="company">
                    Arquivos da empresa
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
              md={2}
            >
              <FormControl
                variant="outlined"
                size="small"
                fullWidth
              >
                <InputLabel>
                  Tipo
                </InputLabel>

                <Select
                  value={type}
                  onChange={
                    resetPage(setType)
                  }
                  label="Tipo"
                >
                  <MenuItem value="all">
                    Todos
                  </MenuItem>

                  <MenuItem value="image">
                    Fotos
                  </MenuItem>

                  <MenuItem value="video">
                    Vídeos
                  </MenuItem>

                  <MenuItem value="audio">
                    Áudios
                  </MenuItem>

                  <MenuItem value="document">
                    Documentos
                  </MenuItem>

                  <MenuItem value="other">
                    Outros
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
              md={4}
            >
              <FormControl
                variant="outlined"
                size="small"
                fullWidth
              >
                <InputLabel>
                  Ordenar
                </InputLabel>

                <Select
                  value={sort}
                  onChange={
                    resetPage(setSort)
                  }
                  label="Ordenar"
                >
                  <MenuItem value="size-desc">
                    Maior tamanho primeiro
                  </MenuItem>

                  <MenuItem value="size-asc">
                    Menor tamanho primeiro
                  </MenuItem>

                  <MenuItem value="date-desc">
                    Mais recente primeiro
                  </MenuItem>

                  <MenuItem value="date-asc">
                    Mais antigo primeiro
                  </MenuItem>

                  <MenuItem value="name-asc">
                    Nome A-Z
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {scope === "company" && (
              <Grid
                item
                xs={12}
                md={4}
              >
                <FormControl
                  variant="outlined"
                  size="small"
                  fullWidth
                >
                  <InputLabel>
                    Origem
                  </InputLabel>

                  <Select
                    value={source}
                    onChange={
                      resetPage(setSource)
                    }
                    label="Origem"
                  >
                    <MenuItem value="">
                      Todas
                    </MenuItem>

                    {data.sources.map(
                      item => (
                        <MenuItem
                          key={item.source}
                          value={item.source}
                        >
                          {item.label}
                          {" — "}
                          {formatBytes(
                            item.bytes
                          )}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid
              item
              xs={12}
              sm={6}
              md={2}
            >
              <TextField
                label="De"
                type="date"
                variant="outlined"
                size="small"
                fullWidth
                value={dateFrom}
                onChange={event => {
                  setDateFrom(
                    event.target.value
                  );
                  setPage(1);
                }}
                InputLabelProps={{
                  shrink: true
                }}
              />
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
              md={2}
            >
              <TextField
                label="Até"
                type="date"
                variant="outlined"
                size="small"
                fullWidth
                value={dateTo}
                onChange={event => {
                  setDateTo(
                    event.target.value
                  );
                  setPage(1);
                }}
                InputLabelProps={{
                  shrink: true
                }}
              />
            </Grid>

            <Grid
              item
              xs={6}
              md={2}
            >
              <TextField
                label="Mínimo MB"
                type="number"
                variant="outlined"
                size="small"
                fullWidth
                value={minSizeMB}
                onChange={event => {
                  setMinSizeMB(
                    event.target.value
                  );
                  setPage(1);
                }}
                inputProps={{
                  min: 0
                }}
              />
            </Grid>

            <Grid
              item
              xs={6}
              md={2}
            >
              <TextField
                label="Máximo MB"
                type="number"
                variant="outlined"
                size="small"
                fullWidth
                value={maxSizeMB}
                onChange={event => {
                  setMaxSizeMB(
                    event.target.value
                  );
                  setPage(1);
                }}
                inputProps={{
                  min: 0
                }}
              />
            </Grid>
          </Grid>

          <div className={classes.periodActions}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => selectOlderThan(30)}
            >
              Mais de 30 dias
            </Button>

            <Button
              size="small"
              variant="outlined"
              onClick={() => selectOlderThan(60)}
            >
              Mais de 60 dias
            </Button>

            <Button
              size="small"
              variant="outlined"
              onClick={() => selectOlderThan(90)}
            >
              Mais de 90 dias
            </Button>

            <Button
              size="small"
              variant="outlined"
              onClick={() => selectOlderThan(180)}
            >
              Mais de 6 meses
            </Button>

            <Button
              size="small"
              variant="outlined"
              onClick={() => selectOlderThan(365)}
            >
              Mais de 1 ano
            </Button>

            {(dateFrom || dateTo) && (
              <Button
                size="small"
                onClick={clearPeriod}
              >
                Limpar período
              </Button>
            )}
          </div>
        </Paper>

        {!loading && data.count > 0 && (
          <>
            {!selectAllFiltered ? (
              <Paper
                variant="outlined"
                style={{
                  padding: 12,
                  marginBottom: 16,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 10
                }}
              >
                <div>
                  <Typography variant="body2">
                    <strong>
                      {data.count.toLocaleString("pt-BR")} arquivo(s)
                    </strong>
                    {" • "}
                    {formatBytes(data.filteredBytes)}
                    {" neste filtro"}
                  </Typography>

                  {(dateFrom || dateTo) && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                    >
                      Período aplicado:
                      {" "}
                      {dateFrom || "início"}
                      {" até "}
                      {dateTo || "hoje"}
                    </Typography>
                  )}
                </div>

                <Button
                  variant="contained"
                  color="primary"
                  disabled={
                    scope !== "conversation"
                  }
                  onClick={() =>
                    setSelectAllFiltered(true)
                  }
                >
                  {scope === "conversation"
                    ? "Selecionar todos deste filtro"
                    : "Escolha Mídias de conversa"}
                </Button>
              </Paper>
            ) : (
              <Paper
                className={classes.selectionBar}
              >
                <div
                  className={classes.selectionInfo}
                >
                  <Typography variant="subtitle1">
                    <strong>
                      {data.count.toLocaleString("pt-BR")}
                      {" arquivo(s) selecionado(s)"}
                    </strong>
                  </Typography>

                  <Typography variant="body2">
                    {formatBytes(data.filteredBytes)}
                    {" serão liberados"}
                  </Typography>

                  <Typography
                    variant="caption"
                    color="textSecondary"
                  >
                    A seleção inclui todos os arquivos
                    que correspondem ao filtro atual,
                    não apenas os desta página.
                  </Typography>
                </div>

                <div>
                  <Button
                    onClick={() =>
                      setSelectAllFiltered(false)
                    }
                    style={{
                      marginRight: 8
                    }}
                  >
                    Cancelar seleção
                  </Button>

                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleDeleteFiltered}
                  >
                    Excluir selecionados
                  </Button>
                </div>
              </Paper>
            )}
          </>
        )}

        {selectedCount > 0 && (
          <Paper
            className={classes.selectionBar}
            style={{
              marginBottom: 16
            }}
          >
            <div
              className={classes.selectionInfo}
            >
              <Typography variant="subtitle1">
                <strong>
                  {selectedCount.toLocaleString("pt-BR")}
                  {" arquivo(s) escolhido(s)"}
                </strong>
              </Typography>

              <Typography variant="body2">
                {formatBytes(selectedBytes)}
                {" serão liberados"}
              </Typography>

              <Typography
                variant="caption"
                color="textSecondary"
              >
                A seleção manual é mantida ao trocar de página.
              </Typography>
            </div>

            <div>
              <Button
                onClick={selectCurrentPage}
                style={{
                  marginRight: 8
                }}
              >
                Selecionar esta página
              </Button>

              <Button
                onClick={clearManualSelection}
                style={{
                  marginRight: 8
                }}
              >
                Limpar seleção
              </Button>

              <Button
                variant="contained"
                color="secondary"
                onClick={handleDeleteSelected}
              >
                Excluir escolhidos
              </Button>
            </div>
          </Paper>
        )}

        {loading ? (
          <Box
            className={classes.loading}
          >
            <CircularProgress />
          </Box>
        ) : data.items.length === 0 ? (
          <Paper
            variant="outlined"
            className={classes.empty}
          >
            Nenhum arquivo encontrado
            com estes filtros.
          </Paper>
        ) : (
          <Grid
            container
            spacing={2}
          >
            {data.items.map(item => (
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
                lg={2}
                key={item.relativePath}
              >
                <Card
                  className={classes.mediaCard}
                  variant="outlined"
                >
                  {item.scope === "conversation" && (
                    <Box
                      position="absolute"
                      top={4}
                      right={4}
                      zIndex={3}
                      bgcolor="background.paper"
                      borderRadius="50%"
                      boxShadow={1}
                    >
                      <Checkbox
                        color="primary"
                        checked={
                          Boolean(
                            selectedItems[
                              item.relativePath
                            ]
                          )
                        }
                        onChange={() =>
                          toggleSelectedItem(
                            item
                          )
                        }
                        inputProps={{
                          "aria-label":
                            `Selecionar ${item.name}`
                        }}
                      />
                    </Box>
                  )}

                  <div
                    className={classes.preview}
                  >
                    <MediaPreview
                      item={item}
                      classes={classes}
                    />
                  </div>

                  <CardContent
                    style={{
                      padding: "10px 12px 4px"
                    }}
                  >
                    <Typography
                      className={classes.filename}
                      title={item.name}
                    >
                      {item.name}
                    </Typography>

                    <Typography
                      className={classes.meta}
                    >
                      {formatBytes(
                        item.size
                      )}
                      {" • "}
                      {new Date(
                        item.date
                      ).toLocaleString(
                        "pt-BR"
                      )}
                    </Typography>

                    <div
                      className={classes.chips}
                    >
                      <Chip
                        size="small"
                        label={
                          item.scope ===
                          "conversation"
                            ? "Conversa"
                            : "Empresa"
                        }
                        variant="outlined"
                      />

                      <Chip
                        size="small"
                        label={
                          typeLabel(
                            item.type
                          )
                        }
                        variant="outlined"
                      />

                      {item.scope ===
                        "company" && (
                        <Chip
                          size="small"
                          label={
                            item.sourceLabel
                          }
                        />
                      )}
                    </div>
                  </CardContent>

                  <Box flexGrow={1} />

                  <CardActions
                    style={{
                      padding: "2px 8px 6px"
                    }}
                  >
                    <Button
                      size="small"
                      component="a"
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={
                        <OpenInNew />
                      }
                    >
                      Abrir
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {!loading &&
          data.count > 0 && (
          <Box
            className={
              classes.pagination
            }
          >
            <Button
              variant="outlined"
              disabled={page <= 1}
              onClick={() =>
                setPage(
                  current =>
                    Math.max(
                      1,
                      current - 1
                    )
                )
              }
            >
              Anterior
            </Button>

            <Typography>
              Página {page}
            </Typography>

            <Button
              variant="outlined"
              disabled={!data.hasMore}
              onClick={() =>
                setPage(
                  current =>
                    current + 1
                )
              }
            >
              Próxima
            </Button>
          </Box>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Storage;
